/**
 * Command palette opened via Ctrl/Cmd+K. Phase 6 polish; SASADM-0106 (saas-auth
 * SASATH-0212) moved its tenant and user lookup to the server.
 *
 * Tenants and users are searched **server-side** — one debounced
 * `listPaged({ q, size: 20 })` per typing burst — so opening the palette never
 * full-fetches the user table, which is the largest on the platform. Brands are
 * a small bounded list and are still fetched once on open and filtered
 * client-side, as are the static create-actions. An empty (or one-character)
 * query fetches nothing at all and shows just those actions.
 */
import {
  Box,
  Dialog,
  DialogContent,
  InputBase,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import type { BrandDto, TenantDto, UserSummaryDto } from '@/api/types';
import { useCopy } from '@/hooks/useCopy';

/** Idle time in the search box before the term is sent to the server. */
const SEARCH_DEBOUNCE_MS = 250;
/** Shortest term worth a round-trip — below this the palette does not search. */
const MIN_QUERY_LENGTH = 2;
/** Rows requested per entity. The palette is a jump-to, not a browser. */
const SEARCH_SIZE = 20;

/** Stable empty results, so a non-searching render doesn't churn identities. */
const NO_USERS: UserSummaryDto[] = [];
const NO_TENANTS: TenantDto[] = [];

interface Item {
  label: string;
  hint?: string;
  to: string;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // The debounced term — this is what the server actually sees.
  const [q, setQ] = useState('');
  const [brands, setBrands] = useState<BrandDto[]>([]);
  const [brandsLoaded, setBrandsLoaded] = useState(false);
  const [foundTenants, setFoundTenants] = useState<TenantDto[]>(NO_TENANTS);
  const [foundUsers, setFoundUsers] = useState<UserSummaryDto[]>(NO_USERS);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { t } = useCopy();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Brands only: a bounded list, fetched once on first open and filtered in the
  // browser. Tenants and users are deliberately NOT part of this load.
  useEffect(() => {
    if (!open || brandsLoaded) return;
    void apiAdmin.brands
      .list()
      .catch((): BrandDto[] => [])
      .then((br) => {
        setBrands(br);
        setBrandsLoaded(true);
      });
  }, [open, brandsLoaded]);

  // Sync the box into `q` once the user stops typing. Whitespace is trimmed and
  // nothing else is: `%`, `_` and `\` are matched literally by the server, so
  // escaping them would corrupt the search.
  useEffect(() => {
    const id = setTimeout(() => setQ(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  // Server search. Both reads go out in parallel and each degrades to no rows,
  // the way the previous full-fetch did. The cleanup flag is the out-of-order
  // guard: a response for a term that is no longer current is dropped rather
  // than rendered.
  useEffect(() => {
    if (!open || q.length < MIN_QUERY_LENGTH) return;
    let current = true;
    void (async () => {
      const [tenants, users] = await Promise.all([
        apiAdmin.tenants
          .listPaged({ q, size: SEARCH_SIZE })
          .then((res) => res.content)
          .catch((): TenantDto[] => []),
        apiAdmin.users
          .listPaged({ q, size: SEARCH_SIZE })
          .then((res) => res.content)
          .catch((): UserSummaryDto[] => []),
      ]);
      if (current) {
        setFoundTenants(tenants);
        setFoundUsers(users);
      }
    })();
    return () => {
      current = false;
    };
  }, [open, q]);

  useEffect(() => {
    if (open) {
      const id = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const term = query.trim().toLowerCase();
  // Below the search threshold the last server results are simply not shown —
  // derived rather than cleared, so no effect has to write state to hide them.
  const searching = term.length >= MIN_QUERY_LENGTH;
  const tenantRows = searching ? foundTenants : NO_TENANTS;
  const userRows = searching ? foundUsers : NO_USERS;

  function matches(it: Item): boolean {
    return it.label.toLowerCase().includes(term) || (it.hint ?? '').toLowerCase().includes(term);
  }

  const actions: Item[] = [
    { label: t('tenants.list.create', 'Create tenant'), to: '/workflows/new-tenant' },
    { label: t('brands.list.create', 'Create brand'), to: '/brands/new' },
  ];

  // With an empty box the palette offers only what you can do, not everything
  // that exists — the entity rows all require a term.
  const items: Item[] = term
    ? [
        ...actions.filter(matches),
        ...tenantRows.map((tn) => ({
          label: tn.name,
          hint: `tenant · ${tn.slug}`,
          to: `/tenants/${tn.id}`,
        })),
        ...brands
          .map((b) => ({
            label: b.displayName,
            hint: `brand · ${b.slug}`,
            to: `/brands/${b.id}`,
          }))
          .filter(matches),
        ...userRows.map((u) => ({
          label: u.name ?? u.email,
          hint: `user · ${u.email}`,
          to: `/users/${u.id}`,
        })),
      ]
    : actions;

  function go(item: Item) {
    setOpen(false);
    setQuery('');
    navigate(item.to);
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <InputBase
            inputRef={inputRef}
            fullWidth
            placeholder={t('common.search', 'Search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ fontSize: '1rem' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && items[0]) go(items[0]);
              if (e.key === 'Escape') setOpen(false);
            }}
          />
        </Box>
        <List sx={{ maxHeight: 360, overflow: 'auto' }}>
          {items.length === 0 ? (
            <ListItem>
              <ListItemText primary={t('palette.noMatches', 'No matches')} />
            </ListItem>
          ) : (
            items.map((it, i) => (
              <ListItemButton key={i} onClick={() => go(it)}>
                <ListItemText primary={it.label} secondary={it.hint} />
              </ListItemButton>
            ))
          )}
          {term ? null : (
            <ListItem>
              <ListItemText
                secondary={t('palette.hint', 'Type to search tenants, brands and users')}
              />
            </ListItem>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
}
